import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, Search, Phone, CalendarDays, ShoppingCart, Tag, Award, Trash2, Plus } from "lucide-react";
import api, { formatPrice, isSuperadmin } from "@/lib/api";

const TAG_OPTIONS = ["VIP", "Постоянный", "Новый", "Проблемный"];

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editNotes, setEditNotes] = useState("");
  const [editTags, setEditTags] = useState([]);
  const [pointsDelta, setPointsDelta] = useState("");
  const [pointsReason, setPointsReason] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", phone: "", notes: "", loyalty_points: 0 });
  const [creating, setCreating] = useState(false);
  const canDelete = isSuperadmin();

  const load = useCallback(() => {
    setLoading(true);
    const params = search ? `?search=${encodeURIComponent(search)}` : "";
    api.get(`/admin/clients${params}`).then((r) => setClients(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const openClient = async (client) => {
    setSelected(client);
    setDetailLoading(true);
    try {
      const r = await api.get(`/admin/clients/${client.id}`);
      setDetail(r.data);
      setEditNotes(r.data.client.notes || "");
      setEditTags(r.data.client.tags || []);
    } catch { }
    setDetailLoading(false);
  };

  const saveClient = async () => {
    if (!selected) return;
    await api.put(`/admin/clients/${selected.id}`, { notes: editNotes, tags: editTags });
    load();
  };

  const toggleTag = (tag) => {
    setEditTags(editTags.includes(tag) ? editTags.filter(t => t !== tag) : [...editTags, tag]);
  };

  const deleteClient = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Удалить клиента? История заказов и бронирований сохранится.")) return;
    try {
      await api.delete(`/admin/clients/${id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Ошибка удаления");
    }
  };

  const clearAllClients = async () => {
    if (!window.confirm(`Очистить ВСЮ базу клиентов? Будет удалено ${clients.length} клиентов. Это действие нельзя отменить.`)) return;
    if (!window.confirm("Вы уверены? Подтвердите ещё раз.")) return;
    try {
      const r = await api.delete("/admin/clients/clear");
      alert(`Удалено клиентов: ${r.data.deleted}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Ошибка");
    }
  };

  const totalSpentAll = clients.reduce((s, c) => s + (c.total_spent || 0), 0);

  const adjustPoints = async () => {
    const delta = parseInt(pointsDelta, 10);
    if (!selected || isNaN(delta) || delta === 0) {
      alert("Укажите число баллов (положительное для начисления, отрицательное для списания)");
      return;
    }
    try {
      const r = await api.post(`/admin/loyalty/points/${selected.id}`, { points: delta, reason: pointsReason });
      // Refresh detail
      const det = await api.get(`/admin/clients/${selected.id}`);
      setDetail(det.data);
      setPointsDelta("");
      setPointsReason("");
      load();
      alert(`Баланс: ${r.data.new_total} (${r.data.tier})`);
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка");
    }
  };

  const createClient = async () => {
    if (!newClient.name.trim() || !newClient.phone.trim()) {
      alert("Имя и телефон обязательны");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/clients", {
        ...newClient,
        loyalty_points: parseInt(newClient.loyalty_points, 10) || 0,
      });
      setCreateOpen(false);
      setNewClient({ name: "", phone: "", notes: "", loyalty_points: 0 });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка");
    }
    setCreating(false);
  };

  return (
    <div data-testid="admin-clients">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Клиенты</h1>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <span className="text-xs text-muted-foreground">Общая выручка</span>
            <p className="text-xl font-serif text-primary">{formatPrice(totalSpentAll)}</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground rounded-none gap-2" data-testid="create-client-btn">
                <Plus className="w-4 h-4" /> Добавить клиента
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10 max-w-md">
              <DialogHeader><DialogTitle className="font-serif">Новый клиент</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-4">
                <Input placeholder="Имя *" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="new-client-name" />
                <Input placeholder="Телефон *" value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="new-client-phone" />
                <Textarea placeholder="Заметка (опц.)" value={newClient.notes} onChange={(e) => setNewClient({ ...newClient, notes: e.target.value })} className="bg-background border-white/10 rounded-none min-h-[60px]" />
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Стартовые баллы лояльности</label>
                  <Input type="number" placeholder="0" value={newClient.loyalty_points || ""} onChange={(e) => setNewClient({ ...newClient, loyalty_points: e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0 })} onFocus={(e) => e.target.select()} className="bg-background border-white/10 rounded-none" />
                </div>
                <Button onClick={createClient} disabled={creating} className="w-full bg-primary text-primary-foreground rounded-none" data-testid="new-client-submit">
                  {creating ? "Создание..." : "Создать"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button onClick={clearAllClients} variant="outline" className={`rounded-none border-red-500/30 text-red-400 hover:bg-red-500/10 gap-1.5 text-xs ${canDelete ? "" : "hidden"}`} data-testid="clear-clients-btn">
            <Trash2 className="w-3.5 h-3.5" /> Очистить базу
          </Button>
        </div>
      </div>

      <div className="relative mb-6">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Поиск по имени или телефону..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-background border-white/10 rounded-none pl-10"
          data-testid="client-search"
        />
      </div>

      <p className="text-xs text-muted-foreground mb-3">Всего: {clients.length}</p>

      {loading && clients.length === 0 ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-card animate-pulse" />)}</div>
      ) : clients.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="no-clients">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-20" />
          <p>Нет клиентов</p>
        </div>
      ) : (
        <div className="space-y-2">
          {clients.map((c) => (
            <div key={c.id} className="relative">
              <button
                onClick={() => openClient(c)}
                className="w-full text-left bg-card border border-white/5 p-4 hover:border-primary/20 transition-all"
                data-testid={`client-${c.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-serif text-lg">
                      {c.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-sans font-medium">{c.name}</span>
                        {c.tags?.map((t) => (
                          <span key={t} className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary">{t}</span>
                        ))}
                      </div>
                      <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>
                        <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{c.total_bookings || 0} бронирований</span>
                        <span className="flex items-center gap-1"><ShoppingCart className="w-3 h-3" />{c.total_orders || 0} заказов</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className="text-primary font-serif text-lg">{formatPrice(c.total_spent || 0)}</span>
                      <p className="text-[10px] text-muted-foreground">потрачено</p>
                    </div>
                  </div>
                </div>
              </button>
              {canDelete && (
                <button
                  onClick={(e) => deleteClient(c.id, e)}
                  className="absolute right-3 top-3 px-2 py-1 text-xs text-red-400 border border-red-500/30 hover:bg-red-500/10 flex items-center gap-1"
                  title="Удалить клиента"
                  data-testid={`delete-client-${c.id}`}
                >
                  <Trash2 className="w-3 h-3" /> Удалить
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Client Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-serif text-lg">
                {selected?.name?.[0]?.toUpperCase()}
              </div>
              {selected?.name}
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="py-8 text-center text-muted-foreground">Загрузка...</div>
          ) : detail ? (
            <div className="space-y-6 mt-4">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-background border border-border p-3 text-center">
                  <p className="text-2xl font-serif text-primary">{detail.client.total_bookings || 0}</p>
                  <p className="text-xs text-muted-foreground">Бронирований</p>
                </div>
                <div className="bg-background border border-border p-3 text-center">
                  <p className="text-2xl font-serif text-primary">{detail.client.total_orders || 0}</p>
                  <p className="text-xs text-muted-foreground">Заказов</p>
                </div>
                <div className="bg-background border border-border p-3 text-center">
                  <p className="text-2xl font-serif text-primary">{formatPrice(detail.client.total_spent || 0)}</p>
                  <p className="text-xs text-muted-foreground">Потрачено</p>
                </div>
                <div className="bg-background border border-border p-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Award className="w-4 h-4 text-yellow-500" />
                    <p className="text-2xl font-serif text-primary">{(detail.client.loyalty_points || 0).toLocaleString()}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">{detail.client.loyalty_tier || "Bronze"}</p>
                </div>
              </div>

              {/* Contact */}
              <div className="flex gap-4 text-sm">
                <span className="flex items-center gap-1 text-muted-foreground"><Phone className="w-3 h-3" />{detail.client.phone}</span>
                <span className="text-muted-foreground text-xs">С {new Date(detail.client.created_at).toLocaleDateString("ru-RU")}</span>
              </div>

              {/* Tags */}
              <div>
                <label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1"><Tag className="w-3 h-3" /> Теги</label>
                <div className="flex flex-wrap gap-2">
                  {TAG_OPTIONS.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`text-xs px-3 py-1 border transition-all ${editTags.includes(tag) ? "border-primary bg-primary/10 text-primary" : "border-white/10 text-muted-foreground hover:border-primary/30"}`}
                      data-testid={`tag-${tag}`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs text-muted-foreground mb-2 block">Заметки</label>
                <Textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Заметки о клиенте..."
                  className="bg-background border-white/10 rounded-none min-h-[60px]"
                  data-testid="client-notes"
                />
              </div>

              <Button onClick={saveClient} className="bg-primary text-primary-foreground rounded-none w-full" data-testid="save-client-btn">
                Сохранить
              </Button>

              {/* Loyalty editor */}
              <div className="bg-primary/5 border border-primary/20 p-3">
                <h3 className="font-sans font-semibold text-sm uppercase tracking-widest mb-2 flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> Лояльность
                </h3>
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-muted-foreground">Баланс: <span className="text-primary font-semibold">{detail.client.loyalty_points || 0} баллов</span></span>
                  <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary">{detail.client.loyalty_tier || "Bronze"}</span>
                </div>
                <div className="grid grid-cols-[1fr_2fr_auto] gap-2">
                  <Input type="number" placeholder="±баллы" value={pointsDelta} onChange={(e) => setPointsDelta(e.target.value)} onFocus={(e) => e.target.select()} className="bg-background border-white/10 rounded-none text-sm" data-testid="points-delta-input" />
                  <Input placeholder="Причина (опц.)" value={pointsReason} onChange={(e) => setPointsReason(e.target.value)} className="bg-background border-white/10 rounded-none text-sm" data-testid="points-reason-input" />
                  <Button onClick={adjustPoints} className="bg-primary text-primary-foreground rounded-none text-xs" data-testid="adjust-points-btn">Применить</Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Положительное число — начислить, отрицательное — списать</p>
              </div>

              {/* Bookings History */}
              {detail.bookings.length > 0 && (
                <div>
                  <h3 className="font-sans font-semibold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-primary" /> Бронирования
                  </h3>
                  <div className="space-y-1">
                    {detail.bookings.map((b) => (
                      <div key={b.id} className="flex items-center justify-between bg-background border border-white/5 p-3 text-sm">
                        <div>
                          <span>{b.house_name_ru || b.house_name || "—"}</span>
                          <span className="text-xs text-muted-foreground ml-2">{b.date} {b.start_hour}:00</span>
                        </div>
                        <span className="text-primary font-semibold">{formatPrice(b.total_amount || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Orders History */}
              {detail.orders.length > 0 && (
                <div>
                  <h3 className="font-sans font-semibold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-primary" /> Заказы
                  </h3>
                  <div className="space-y-1">
                    {detail.orders.map((o) => (
                      <div key={o.id} className="flex items-center justify-between bg-background border border-white/5 p-3 text-sm">
                        <div>
                          <span>{o.house_name_ru || "—"}</span>
                          <span className="text-xs text-muted-foreground ml-2">{new Date(o.created_at).toLocaleDateString("ru-RU")}</span>
                          <span className="text-xs text-muted-foreground ml-1">({o.items?.length || 0} поз.)</span>
                        </div>
                        <span className="text-primary font-semibold">{formatPrice(o.total_amount || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
